import _ from 'lodash';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Icon from 'react-icons-kit';
import { ic_check_box as checkboxChecked } from 'react-icons-kit/md/ic_check_box';
import SimpleReactLightbox from 'simple-react-lightbox';
import { SRLWrapper } from 'simple-react-lightbox';
import { ic_check_box_outline_blank as checkboxUnchecked } from 'react-icons-kit/md/ic_check_box_outline_blank';
import { ic_attach_file as attachmentIcon } from 'react-icons-kit/md/ic_attach_file';
import { ic_sync_problem as syncIssue } from 'react-icons-kit/md/ic_sync_problem';
import { download as downloadIcon } from 'react-icons-kit/ikons/download';
import moment from 'moment';
import { renderFileName, getLockedLabel } from '../../../utils/CommonFunctions.js';
import AttachmentsView from './AttachmentsView';
import { withFeatureFlag } from '../../../hocs/withFeatureFlag.js';

const MAX_DESCRIPTION_LENGTH = 160;
const entityNameToDisplayName = {
    vendor: 'Vendor',
    customer: 'Customer',
    qbClass: 'Class',
    qbAccount: 'Account',
};

class ExpenseDetailsRow extends Component {
    static propTypes = {
        id: PropTypes.string.isRequired,
        vendorName: PropTypes.string.isRequired,
        customerName: PropTypes.string.isRequired,
        accountName: PropTypes.string,
        itemName: PropTypes.string,
        serviceName: PropTypes.any,
        bill: PropTypes.any,
        description: PropTypes.string,
        note: PropTypes.string,
        date: PropTypes.string.isRequired,
        amount: PropTypes.string.isRequired,
        isBillable: PropTypes.bool.isRequired,
        isSynced: PropTypes.bool.isRequired,
        isApprovedForSyncing: PropTypes.bool.isRequired,
        isEditing: PropTypes.bool.isRequired,
        isSelected: PropTypes.bool.isRequired,
        isUpdatingBillable: PropTypes.bool.isRequired,
        isUpdatingStatus: PropTypes.bool.isRequired,
        attachments: PropTypes.array,
        shouldShowQbClasses: PropTypes.bool.isRequired,
        shouldShowBillable: PropTypes.bool.isRequired,
        canApprove: PropTypes.bool.isRequired,
        canEdit: PropTypes.bool.isRequired,
        canLockEntries: PropTypes.bool,
        entryOptions: PropTypes.node.isRequired,
        noIntegration: PropTypes.bool,
        isLocked: PropTypes.bool,
        readOnly: PropTypes.bool,
        accountStore: PropTypes.object,
    };

    static defaultProps = {
        attachments: [],
    };

    state = {
        isShowingAttachments: false,
        isDescriptionExpanded: false,
    };

    constructUnknownEntitiesDataTip = unknownEntities => {
        let displayNames = [];
        unknownEntities.forEach(entityName => {
            displayNames.push(entityNameToDisplayName[entityName]);
        });
        return 'Unknown ' + displayNames.join(', ');
    };

    handleAttachmentsTogglePressed = () => {
        this.setState({ isShowingAttachments: !this.state.isShowingAttachments });
    };

    renderAttachmentView = attachments => {
        let attachmentSection = [];
        for (let index in attachments) {
            const attachment = attachments[index];
            attachmentSection.push(
                <a
                    onClick={e => this.handleAttachmentClick(e, attachment)}
                    key={attachment.id}
                    className="eAttachmentContainer"
                    href={attachment.url}
                >
                    <img
                        ref={this.imgRef}
                        alt={attachment.name || 'attachment'}
                        className="eAttachmentImage"
                        src={attachment.web_thumbnail_url}
                    />

                    <span className="eAttachmentFilename">
                        {renderFileName(attachment.name, attachment.type)}
                    </span>
                    <Icon icon={downloadIcon} size={24} className="eAttachmentDownloadIcon" />
                </a>
            );
        }
        return (
            <SimpleReactLightbox>
                <SRLWrapper
                    options={{
                        thumbnails: { showThumbnails: false },
                        buttons: {
                            showAutoplayButton: false,
                        },
                    }}
                >
                    <div className="eAttachmentsContainer">{attachmentSection}</div>
                </SRLWrapper>
            </SimpleReactLightbox>
        );
    };

    render() {
        const {
            vendorName,
            customerName,
            expenseableName,
            serviceName,
            bill,
            description,
            note,
            attachments,
            date,
            amount,
            isSynced,
            isApprovedForSyncing,
            isBillable,
            isEditing,
            isSelected,
            isUpdatingBillable,
            isUpdatingStatus,
            shouldShowQbClasses,
            shouldShowBillable,
            onEntrySelect,
            onBillablePress,
            onToggleApprovePress,
            entryOptions,
            unknownEntities,
            isAbleToSync,
            canApprove,
            canEdit,
            canLockEntries,
            noIntegration,
            isLocked,
            readOnly,
            syncError,
            accountStore,
        } = this.props;

        const { isShowingAttachments, isDescriptionExpanded } = this.state;
        const formattedDate = moment(date).format('MMM D, YYYY');

        const shouldTruncate = description ? description.length > MAX_DESCRIPTION_LENGTH : false;
        const truncatedDescription =
            shouldTruncate && !isDescriptionExpanded
                ? _.truncate(description, { length: MAX_DESCRIPTION_LENGTH, separator: ' ' })
                : description;
        const approvedText = noIntegration ? 'Approved' : 'Approved for Sync';
        return (
            <tr
                className={classNames({
                    detailsRow: true,
                    editing: isEditing,
                    selected: isSelected,
                })}
            >
                <td className="eSelectionCell">
                    {(canEdit || canApprove) && isAbleToSync && (
                        <Icon
                            className="icon"
                            icon={isSelected ? checkboxChecked : checkboxUnchecked}
                            size={18}
                            onClick={onEntrySelect}
                        />
                    )}

                    {!isAbleToSync && !isSynced && (
                        <span
                            sync-data-tip={
                                unknownEntities.length > 0
                                    ? this.constructUnknownEntitiesDataTip(unknownEntities)
                                    : syncError
                            }
                        >
                            <Icon className="syncIssueIcon" icon={syncIssue} size={30} />
                        </span>
                    )}
                </td>
                <td className="eVendorValueCell">{vendorName}</td>
                <td className="eCustomerJobDescriptionCell">
                    <strong className="eCustomerName">{customerName}</strong>

                    {expenseableName && <span className="eAccountName">{expenseableName}</span>}
                    {
                        <span
                            className={classNames({
                                eBillableTag: true,
                                eEditableBillableTag: canEdit && shouldShowBillable,
                                isBillable,
                            })}
                            onClick={canEdit && shouldShowBillable ? onBillablePress : null}
                        >
                            {isUpdatingBillable
                                ? 'Updating...'
                                : isBillable
                                ? 'Billable'
                                : 'Not Billable'}
                        </span>
                    }

                    {shouldShowQbClasses && serviceName && (
                        <span className="eServiceName">{serviceName}</span>
                    )}

                    {bill && <span className="eBill">{bill}</span>}

                    {!this.props.featureFlag?.hide_description_field && truncatedDescription && (
                        <p className="entryDescriptionContainer">
                            {truncatedDescription}{' '}
                            {shouldTruncate && (
                                <button
                                    onClick={() =>
                                        this.setState({
                                            isDescriptionExpanded: !isDescriptionExpanded,
                                        })
                                    }
                                    className="tertiaryButton"
                                >
                                    {isDescriptionExpanded ? 'Less' : 'More'}
                                </button>
                            )}
                        </p>
                    )}

                    {attachments.length > 0 && (
                        <button
                            className="tertiaryButton eViewAttachmentsButton"
                            onClick={this.handleAttachmentsTogglePressed}
                        >
                            <Icon icon={attachmentIcon} size={18} className="icon" />

                            {isShowingAttachments ? 'Hide Attachments' : 'View Attachments'}
                        </button>
                    )}

                    {isShowingAttachments && <AttachmentsView attachments={attachments} />}
                </td>
                <td className="eDateCell">{formattedDate}</td>
                <td className="eAmountCell">{amount}</td>
                {this.props.featureFlag && this.props.featureFlag.show_note_field && (
                    <td className="eNoteCell">{note || ''}</td>
                )}
                <td className="eStatusCell">
                    {isSynced && (
                        <span className="eStatus eStatusSynced">
                            {isUpdatingStatus ? 'Updating...' : 'Synced'}
                        </span>
                    )}

                    {isLocked && noIntegration && (
                        <span className="tsStatus tsStatusApproved">
                            {isUpdatingStatus ? 'Updating...' : getLockedLabel(accountStore)}
                        </span>
                    )}

                    {isApprovedForSyncing && !isSynced && canApprove && !isLocked && (
                        <span
                            className="eStatus eStatusApproved"
                            onClick={() => canLockEntries && onToggleApprovePress()}
                        >
                            {isUpdatingStatus ? 'Updating...' : approvedText}
                        </span>
                    )}
                    {!isApprovedForSyncing && !isSynced && canApprove && !isLocked && (
                        <span className="eStatus eStatusPending" onClick={onToggleApprovePress}>
                            {isUpdatingStatus ? 'Updating...' : 'Pending Approval'}
                        </span>
                    )}
                </td>

                {!readOnly && <td className="actionsCell">{entryOptions}</td>}
            </tr>
        );
    }
}

export default withFeatureFlag(ExpenseDetailsRow);
